import { Button } from '@components/atoms/Button';
import { Card } from '@components/atoms/Card';
import { Progress } from '@components/atoms/Progress';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useHistory, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { formatForDisplay } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { useAiPlanningAvailable } from '@hooks/api/projectPlanDrafts';

interface TimelineCardProps {
  startDate: Date | string;
  endDate: Date | string;
  totalManHours?: number | null;
  onImportTasks?: () => void;
}

/**
 * Sprint 31, story-161. The "Generate with AI" gate/handler lives entirely inside this
 * component (not passed down from `projects.tsx`, unlike `onImportTasks`) so `projects.test.tsx`
 * — which mocks this component out wholesale — never has to know about the AI-planning query
 * hook this button depends on.
 */
export const TimelineCard = ({
  startDate,
  endDate,
  totalManHours,
  onImportTasks,
}: TimelineCardProps) => {
  const tProjectComponents = useTranslation('projectComponents');
  const t = tProjectComponents.timelineCard;
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { role } = useAppSelector((state: RootState) => state.user);
  const { data: aiPlanningAvailable } = useAiPlanningAvailable();
  const canGenerateWithAi = roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK && aiPlanningAvailable;
  const goToProjectSchedule = () => {
    history.push(`/schedule/${id}`);
  };

  const { progressValue } = useMemo(() => {
    // Handle date parsing in a timezone-agnostic way for date-only fields
    let parsedStart: Date;
    let parsedEnd: Date;

    if (typeof startDate === 'string') {
      // Parse YYYY-MM-DD format in local timezone to avoid UTC shift
      if (startDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = startDate.split('-').map(Number);
        parsedStart = new Date(year, month - 1, day);
      } else {
        parsedStart = new Date(startDate);
      }
    } else {
      parsedStart = startDate;
    }

    if (typeof endDate === 'string') {
      // Parse YYYY-MM-DD format in local timezone to avoid UTC shift
      if (endDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = endDate.split('-').map(Number);
        parsedEnd = new Date(year, month - 1, day);
      } else {
        parsedEnd = new Date(endDate);
      }
    } else {
      parsedEnd = endDate;
    }

    const now = new Date();
    const startMs = parsedStart instanceof Date ? parsedStart.getTime() : NaN;
    const endMs = parsedEnd instanceof Date ? parsedEnd.getTime() : NaN;
    let value = 0;

    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      const ratio = (now.getTime() - startMs) / (endMs - startMs);
      value = Math.round(Math.min(1, Math.max(0, ratio)) * 100);
    }

    return { progressValue: value };
  }, [startDate, endDate]);
  return (
    <Card className="flex flex-col min-h-[13rem] p-4 w-full rounded-tl-none rounded-bl-none">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <TimelineOutlinedIcon
            sx={{ fontSize: '24px' }}
            className="text-orange-400"
          />
          <h1 className="text-base font-semibold">{t.title}</h1>
        </div>
        {/* Equal-thirds grid: labels share row 1, values share row 2, so a
            wrapping label (e.g. "Total Man-Hours") never misaligns the values. */}
        <div className="grid grid-cols-3 gap-x-3 gap-y-1">
          <h1 className="text-sm font-medium">{t.start}</h1>
          <h1 className="text-sm font-medium">{t.end}</h1>
          <h1 className="text-sm font-medium">{t.totalManHours}</h1>
          <p className="text-sm font-medium truncate">
            {formatForDisplay(startDate)}
          </p>
          <p className="text-sm font-medium truncate">
            {formatForDisplay(endDate)}
          </p>
          <p className="text-sm font-medium truncate">
            {totalManHours ? `${totalManHours.toLocaleString()} h` : '—'}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
            <span>{t.progress}</span>
            <span>
              {formatForDisplay(startDate)} - {formatForDisplay(endDate)}
            </span>
          </div>
          <Progress
            size="sm"
            value={progressValue}
            variant="success"
            showPercentage={false}
          />
        </div>
      </div>

      {/* Actions pinned to the bottom of the card (icon + text). Import and Schedule share the
          width; the AI button sits last and keeps to its own width with a short label so the
          three never overflow the card (the full label lives in the tooltip / aria-label). */}
      <div className="mt-auto flex items-center gap-2 pt-3">
        {onImportTasks && (
          <Button
            variant="tertiary"
            onClick={onImportTasks}
            leftIcon={<AddTaskOutlinedIcon sx={{ fontSize: 18 }} />}
            className="flex-1 !justify-center whitespace-nowrap">
            {t.import}
          </Button>
        )}
        <Button
          variant="tertiary"
          onClick={goToProjectSchedule}
          leftIcon={<ViewTimelineOutlinedIcon sx={{ fontSize: 18 }} />}
          className="flex-1 !justify-center whitespace-nowrap">
          {t.schedule}
        </Button>
        {canGenerateWithAi && (
          <Button
            variant="tertiary"
            onClick={() => history.push(`/projects/${id}/plan/ai`)}
            leftIcon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />}
            className="flex-none !justify-center whitespace-nowrap"
            title={t.generateWithAi}
            aria-label={t.generateWithAi}
            data-testid="timeline-card-generate-with-ai">
            {t.ai}
          </Button>
        )}
      </div>
    </Card>
  );
};
